import { ExtractDocumentTypeFromTypedRxJsonSchema, RxCollection, RxDatabase } from 'rxdb';

import { User } from 'types/user';
import { Survey } from 'types/survey';
import { SurveyResponse } from 'types/surveyResponse';
import { CustomerVisit } from 'types/customer';
import { PendingBlob } from 'types/pendingBlob';
import { Product } from 'types/product';
import { ModifiedDate } from 'types/date';
import {
  CustomerVisitDbSchema,
  ModifiedDateSchema,
  PendingBlobsSchema,
  ProductSchema,
  SurveyResponseSchema,
  SurveySchema,
  UserSchema
} from 'schemas';

export enum ObjectStores {
  User = 'user',
  UnsubmittedSurveys = 'unsubmittedSurveys',
  SubmittedSurveys = 'submittedSurveys',
  CustomerVisits = 'customerVisits',
  Products = 'products',
  ModifiedDates = 'modifiedDates',
  PendingBlobs = 'pendingBlobs'
}

export type DatabaseCollections = {
  [ObjectStores.User]?: RxCollection<ExtractDocumentTypeFromTypedRxJsonSchema<typeof UserSchema>, UserMethods>;
  [ObjectStores.UnsubmittedSurveys]?: RxCollection<
    ExtractDocumentTypeFromTypedRxJsonSchema<typeof SurveySchema>,
    UnsubmittedSurveysMethods
  >;
  [ObjectStores.SubmittedSurveys]?: RxCollection<
    ExtractDocumentTypeFromTypedRxJsonSchema<typeof SurveyResponseSchema>,
    SubmittedSurveysMethods
  >;
  [ObjectStores.CustomerVisits]?: RxCollection<
    ExtractDocumentTypeFromTypedRxJsonSchema<typeof CustomerVisitDbSchema>,
    CustomerVisitsMethods
  >;
  [ObjectStores.Products]?: RxCollection<
    ExtractDocumentTypeFromTypedRxJsonSchema<typeof ProductSchema>,
    ProductsMethods
  >;
  [ObjectStores.ModifiedDates]?: RxCollection<
    ExtractDocumentTypeFromTypedRxJsonSchema<typeof ModifiedDateSchema>,
    ModifiedDatesMethods
  >;
  [ObjectStores.PendingBlobs]?: RxCollection<
    ExtractDocumentTypeFromTypedRxJsonSchema<typeof PendingBlobsSchema>,
    PendingBlobsMethods
  >;
};
export type Database = RxDatabase<DatabaseCollections>;
export type TableValueType = string | boolean | Survey | SurveyResponse | CustomerVisit[] | Product | ModifiedDate;

export type UserMethods = {
  upsertUser: (user: User) => Promise<void>;
  getUser: () => Promise<User | null>;
  deleteUser: (userId: string) => Promise<boolean>;
  purgeUsers: () => Promise<void>;
};

export type UnsubmittedSurveysMethods = {
  upsertSurveys: (surveys: Survey[]) => Promise<boolean>;
  getSurveys: () => Promise<Survey[]>;
  getSurveyByDefinitionId: (definitionId: string) => Promise<Survey | null>;
};

export type SubmittedSurveysMethods = {
  upsertSurveyResponses: (surveyResponses: SurveyResponse[]) => Promise<boolean>;
  getSurveyResponses: () => Promise<SurveyResponse[]>;
  getSurveyResponseById: (responseId: string) => Promise<SurveyResponse | null>;
  deleteSurveyResponse: (responseId: string) => Promise<boolean>;
};

export type CustomerVisitsMethods = {
  upsertCustomerVisits: (customerVisits: CustomerVisit[]) => Promise<boolean>;
  getCustomerVisits: (email: string) => Promise<CustomerVisit[]>;
};

export type ProductsMethods = {
  upsertProducts: (products: Product[]) => Promise<boolean>;
  getProducts: () => Promise<Product[]>;
  getProductById: (productId: string) => Promise<Product | null>;
};

export type ModifiedDatesMethods = {
  upsertModifiedDates: (modifiedDates: ModifiedDate[]) => Promise<boolean>;
};

export type PendingBlobsMethods = {
  upsertBlob: (pendingBlob: PendingBlob) => Promise<boolean>;
  getPendingBlobByName: (name: string) => Promise<PendingBlob | null>;
  deletePendingBlob: (name: string) => Promise<boolean>;
};
